import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Snowflake, MessageCircle, Star, Calendar } from 'lucide-react';
import { TutorProfile } from '@/lib/pocketbase';
import { getProfilePictureUrl, getUserInitials } from '@/utils/profileUtils';
import { useCurrency, currencies } from '@/contexts/CurrencyContext';
import TutorCountryFlag from './TutorCountryFlag';
import { useReviews } from '@/hooks/useReviews';
import { useNavigate } from 'react-router-dom';

interface FrozenTutorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutor: TutorProfile | null;
}

const FrozenTutorModal = ({ isOpen, onClose, tutor }: FrozenTutorModalProps) => {
  const { convertPrice } = useCurrency();
  const { calculateAverageRating } = useReviews(tutor?.id || '');
  const navigate = useNavigate();

  // Format price with tutor's specific currency
  const formatTutorPrice = (price: number) => {
    const tutorCurrency = currencies.find(c => c.code === (tutor?.pricingCurrency || 'USD'));
    if (!tutorCurrency) return `$${price}`;
    
    // Convert from USD to tutor's currency
    const convertedPrice = convertPrice(price);
    const displayPrice = tutor?.pricingCurrency === 'USD' ? price : convertedPrice;
    
    return `${tutorCurrency.symbol}${Math.round(displayPrice)}`;
  };

  if (!tutor) return null;

  const getTutorName = (tutor: TutorProfile) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Unknown Tutor';
  };

  const handleSendMessage = () => {
    // Navigate to tutor details page with message parameter
    navigate(`/tutor/${tutor.id}?openMessage=true`);
    onClose();
  };

  const handleBrowseOtherTutors = () => {
    navigate('/tutors');
    onClose();
  };

  const profilePictureUrl = getProfilePictureUrl(tutor);
  const tutorName = getTutorName(tutor);
  const rating = calculateAverageRating();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center pb-4">
          <div className="mx-auto mb-4 p-3 bg-blue-50 rounded-full w-fit">
            <Snowflake className="h-8 w-8 text-blue-500" />
          </div>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Tutor Not Available
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-base">
            This tutor is currently not accepting new bookings
          </DialogDescription>
        </DialogHeader>

        <Card className="border-0 shadow-none bg-gray-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={profilePictureUrl || ''} alt={tutorName} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getUserInitials(tutorName)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900">{tutorName}</h3>
                  {tutor.country && <TutorCountryFlag countryCode={tutor.country} />}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  {rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                      <span>{rating.toFixed(1)}</span>
                    </div>
                  )}
                  <span className="font-semibold text-primary">
                    {formatTutorPrice(tutor.hourlyRate || 0)}/hr
                  </span>
                </div>
              </div>
            </div>

            {tutor.subjects && tutor.subjects.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tutor.subjects.slice(0, 3).map((subject, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-white text-xs text-gray-600 rounded-md border"
                  >
                    {subject}
                  </span>
                ))}
                {tutor.subjects.length > 3 && (
                  <span className="px-2 py-1 bg-white text-xs text-gray-500 rounded-md border">
                    +{tutor.subjects.length - 3} more
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <div className="text-center text-sm text-gray-600">
            <p className="mb-2">
              <strong>{tutorName}</strong> has temporarily paused their booking schedule.
            </p>
            <p>You can still send them a message to inquire about future availability.</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBrowseOtherTutors}
              className="flex-1"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Browse Other Tutors
            </Button>
            
            <Button
              onClick={handleSendMessage}
              className="flex-1"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FrozenTutorModal;